import * as React from "react";
import { useEffect, useState } from "react";
import { Autocomplete, Checkbox, ListItemText, TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { translate } from "../../translate/translate";
import { ErrorComponent } from "../error/error.component";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";


interface Element {
  [key: string]: any;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const DEFAULT_FILTERS: any[] = [];

const AutocompletePaginated = (props: any) => {
  const {
    keepFilter,
    display,
    searchField,
    label,
    service,
    onSelectElement,
    element,
    styleProps,
    required = false,
    disabled = false,
    clear = false,
    reset = false,
    filters = DEFAULT_FILTERS,
    isMultiSelect = false,
    onEnter,
    forceError = false,
  } = props;
  const limit = 5;
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  const [elements, setElements] = useState<Element[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaginationButtons, setShowPaginationButtons] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>(isMultiSelect ? (element || []) : (element || null));
  const [initialValueSet, setInitialValueSet] = useState(false);
  const [, setFocusAutocomplete] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [internalReset, setInternalReset] = useState(false);

  useEffect(() => {
    if (keepFilter) {
      service.getById(keepFilter).then((response: any) => {
        setSelectedOption(isMultiSelect ? [response.data] : response.data);
      });
    }
  }, []);

  useEffect(() => {
    if (element && !initialValueSet) {
      setSelectedOption(element);
      setElements([element]);
      setInitialValueSet(true);
    }
  }, [element, service, isMultiSelect]);

  useEffect(() => {
    if (element !== undefined && element !== null && !initialValueSet) {
      setSelectedOption(isMultiSelect ? (Array.isArray(element) ? element : [element]) : element);
      setElements(isMultiSelect ? (Array.isArray(element) ? element : [element]) : [element]);
      setInitialValueSet(true);
    }
  }, [element, initialValueSet, isMultiSelect]);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (disabled) return;
    getElements();
  }, [offset, searchTerm, filtersKey, disabled]);

  useEffect(() => {
    if (clear) {
      setSelectedOption(isMultiSelect ? [] : null);
      setInitialValueSet(false);
      setFocusAutocomplete(false);
    }
  }, [clear, isMultiSelect]);

  useEffect(() => {
    if (reset) {
      setInternalReset(true);
      setSelectedOption(isMultiSelect ? [] : null);
      setElements([]);
      setSearchTerm("");
      setOffset(0);
      setInitialValueSet(false);
      setFocusAutocomplete(false);
      setTimeout(() => setInternalReset(false), 500);
    }
  }, [reset, isMultiSelect]);


  const getElements = React.useCallback(() => {
    service.clearParameters();

    const parsedFilters = JSON.parse(filtersKey);
    if (Array.isArray(parsedFilters)) {
      parsedFilters.forEach((filter: any) => {
        const keys = Object.keys(filter);
        if (keys.length > 0) {
          keys.forEach((key) => {
            service.addParameter(key, filter[key]);
          });
        }
      });
    } else if (typeof parsedFilters === "object" && parsedFilters !== null) {
      Object.keys(parsedFilters).forEach(key => {
        service.addParameter(key, parsedFilters[key]);
      });
    }

    if (searchTerm) {
      service.addParameter(searchField, searchTerm);
    }
    service.addParameter("limit", limit);
    service.addParameter("offset", offset);

    service.getAllPaginate().then((response: any) => {
      setCount(response.data.count);
      setElements(response.data.results);
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  }, [filtersKey, searchTerm, searchField, service, limit, offset]);

  const previous = () => {
    if (offset > 0) {
      setOffset(offset - limit);
    }
  };

  const next = () => {
    if ((offset + limit) < count) {
      setOffset(offset + limit);
    }
  };

  const handleAutocompleteChange = (event: React.ChangeEvent<any>, value: any | any[] | null) => {
    setSelectedOption(value);
    if (!isMultiSelect) {
      setOffset(0);
      setSearchTerm("");
    }
    onSelectElement(value);
  };

  const displayValue = (option: any) => {
    if (option === null || option === undefined) {
      return "";
    }
    if (typeof option === "string") {
      return option;
    }
    if (Array.isArray(display)) {
      const values = display.map((item) => option[item] ? translate.t(option[item].toString()) : "");
      return values.filter(Boolean).join(" - ");
    }
    if (typeof display === "string" && display.includes(".")) {
      return display.split(".").reduce((acc, key) => acc && acc[key], option) || "";
    }
    return option[display] ? translate.t(option[display].toString()) : "";
  };

  // A validação visual (borda vermelha) agora só aparece se o erro for forçado (ex: clique no botão pesquisar)
  const isRequiredError = forceError && required && (isMultiSelect ? selectedOption.length === 0 : !selectedOption);

  return (
    <Autocomplete
      sx={styleProps}
      multiple={isMultiSelect}
      disableCloseOnSelect={isMultiSelect}
      disabled={disabled}
      open={open}
      onOpen={() => {
        setShowPaginationButtons(true);
        setOpen(true);
      }}
      onClose={() => {
        setShowPaginationButtons(false);
        setOpen(false);
      }}
      value={selectedOption}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      getOptionLabel={(option) => displayValue(option)}
      closeText={translate.t("close")}
      openText={translate.t("open")}
      clearText={translate.t("clear")}
      noOptionsText={translate.t("empty_listing")}
      options={elements}
      onChange={handleAutocompleteChange}
      onChangeCapture={() => setOffset(0)}
      onBlur={() => {
        setFocusAutocomplete(true);
        if (!selectedOption && searchTerm && elements && elements.length > 0 && !isMultiSelect) {
          const firstOption = elements[0];
          setSelectedOption(firstOption);
          onSelectElement(firstOption);
        }
      }}
      onInputChange={(event, newValue) => {
        if (!isMultiSelect && selectedOption && !newValue.includes(displayValue(selectedOption))) {
          setSelectedOption(null);
          onSelectElement(null);
        }
        setSearchTerm(newValue);
      }}
      renderOption={isMultiSelect ? (props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          <ListItemText primary={displayValue(option)} />
        </li>
      ) : undefined
      }
      className={isRequiredError ? "error-label-autocomplete" : ""}
      renderInput={(params) => (
        <TextField
          className={"autocomplete-paginated"}
          sx={{
            marginTop: 0,
            marginBottom: "3px",
          }}
          {...params}
          margin="normal"
          label={translate.t(label)}
          placeholder={props.placeholder}
          required={required}
          disabled={disabled}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.stopPropagation();
              setOpen(false);
              if (searchTerm && elements && elements.length > 0) {
                if (isMultiSelect) {
                  const alreadySelected = selectedOption.some((opt: any) => opt.id === elements[0].id);
                  if (!alreadySelected) {
                    const newSelected = [...selectedOption, elements[0]];
                    setSelectedOption(newSelected);
                    setTimeout(() => onSelectElement(newSelected), 0);
                  } else {
                    setTimeout(() => onSelectElement(selectedOption), 0);
                  }
                } else {
                  setSelectedOption(elements[0]);
                  setTimeout(() => onSelectElement(elements[0]), 0);
                }
              } else {
                setTimeout(() => onSelectElement(selectedOption), 0);
              }
              if (typeof onEnter === 'function') {
                setTimeout(() => onEnter(), 0);
              }
            }
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          InputProps={{
            ...params.InputProps,
            className: (isRequiredError && !internalReset) ? "error-autocomplete" : "",
            sx: {
              paddingRight: "64px !important",
              paddingTop: "6px",
              paddingBottom: "6px",
              flexWrap: "wrap",
            },
            endAdornment: (
              <React.Fragment>
                {
                  <div
                    className={
                      (!showPaginationButtons || (!offset && (offset + limit) >= count)) || (!isMultiSelect && selectedOption)
                        ? "hide-buttons"
                        : "pagination-buttons"
                    }
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginRight: "25px", marginTop: "5px" }}
                  >
                    <IconButton disabled={!offset} sx={{ padding: "0 0 3px 0" }} onClick={() => previous()}>
                      <ChevronLeft />
                    </IconButton>
                    <IconButton disabled={(offset + limit) >= count} sx={{ padding: "0 0 3px 0" }} onClick={() => next()}>
                      <ChevronRight />
                    </IconButton>
                  </div>
                }
                {(isRequiredError && !internalReset) && (
                  <div className={"required-field"}>
                    {translate.t("required_field")}
                  </div>
                )}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

AutocompletePaginated.displayName = "AutocompletePaginated";

export default AutocompletePaginated;
